import { useCallback, useMemo } from "react";
import { UseRequestPastRecommendationsProps } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestPastRecommendations/requestPastRecommendations.model";
import { getCloudinaryOptimizedUrl } from "@companion-core/shared/app/Utils/cloudinary";
import {
  APIAddress,
  APIProposal,
  APIRecommendation,
} from "@companion-core/shared/app/Interfaces/API";

type PastRecommendation = APIRecommendation & { proposalId: string };

type RecommendationImage = { order: number; url: string; description_alt?: string };

export const useRequestPastRecommendations = ({
  proposalsData,
  contentId,
}: UseRequestPastRecommendationsProps) => {
  const proposals = useMemo(
    () => (proposalsData?.data?.results as APIProposal[] | undefined) ?? [],
    [proposalsData]
  );
  const currentProposal = useMemo(
    () => (contentId ? proposals.find((value) => value.id === contentId) : undefined),
    [proposals, contentId]
  );

  const pastRecommendations = useMemo<PastRecommendation[]>(() => {
    if (contentId) {
      return (
        currentProposal?.recommendations.map((recommendation) => ({
          ...recommendation,
          proposalId: contentId,
        })) ?? []
      );
    }
    return proposals.reduce<PastRecommendation[]>(
      (acc, proposal) =>
        acc.concat(
          proposal.recommendations?.map((recommendation) => ({
            ...recommendation,
            proposalId: proposal.id,
          })) ?? []
        ),
      []
    );
  }, [proposals, currentProposal, contentId]);
  const getProposalId = useCallback(
    (recommendation: PastRecommendation) => recommendation.proposalId,
    []
  );
  const getRecommendationMedia = useCallback((recommendation: APIRecommendation) => {
    const imageUrl = (recommendation?.images as RecommendationImage[] | undefined)?.find(
      (image) => image.order === 1
    )?.url;
    return imageUrl ? getCloudinaryOptimizedUrl(imageUrl, { contentType: "logo" }) : undefined;
  }, []);
  const getRecommendationMediaAlt = useCallback((recommendation: APIRecommendation) => {
    return (recommendation?.images as RecommendationImage[] | undefined)?.find(
      (image) => image.order === 1
    )?.description_alt;
  }, []);
  const getRecommendationSubtitle = useCallback(
    (recommendation: APIRecommendation) => recommendation.summary,
    []
  );
  const getRecommendationBudget = useCallback(
    (recommendation: APIRecommendation) => recommendation.price as string | undefined,
    []
  );
  const getRecommendationBeginningAddress = useCallback(
    (recommendation: APIRecommendation) => recommendation.beginning_address as APIAddress,
    []
  );
  const getRecommendationEndingAddress = useCallback(
    (recommendation: APIRecommendation) => recommendation.ending_address as APIAddress,
    []
  );
  const introduction = useMemo(() => currentProposal?.introduction, [currentProposal]);
  const conclusion = useMemo(() => currentProposal?.conclusion, [currentProposal]);

  return {
    pastRecommendations,
    getProposalId,
    getRecommendationMedia,
    getRecommendationMediaAlt,
    getRecommendationSubtitle,
    getRecommendationBudget,
    getRecommendationBeginningAddress,
    getRecommendationEndingAddress,
    introduction,
    conclusion,
  };
};
